import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, User, Phone, Mail, MapPin, Building2, Home,
  Edit, Save, Loader2, PoundSterling, FileText, Calendar,
  AlertCircle, CheckCircle, Send, Pen, Clock, RotateCcw, Users,
  Plus, Trash2, Shield, BellOff, Settings, UserPlus, Archive
} from "lucide-react";
import { API } from "@/App";
import { ApplicantForm } from "@/components/ApplicantForm";

export const TenantDetailPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [property, setProperty] = useState(null);
  const [landlord, setLandlord] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Archive modal
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  
  // Send Tenancy Agreement Modal
  const [showTAModal, setShowTAModal] = useState(false);
  const [sendingTA, setSendingTA] = useState(false);
  
  // Convert Applicant to Tenant Modal
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [properties, setProperties] = useState([]);
  const [convertData, setConvertData] = useState({
    property_id: "",
    rent_amount: "",
    tenancy_start: "",
    tenancy_end: "",
    deposit_amount: "",
    send_invite: true
  });
  
  // Enhanced Applicant Details Modal
  const [showApplicantModal, setShowApplicantModal] = useState(false);
  const [savingApplicant, setSavingApplicant] = useState(false);
  const [applicantData, setApplicantData] = useState({
    is_business: false,
    business_name: "",
    contacts: [{
      title: "",
      first_name: "",
      last_name: "",
      friendly_name: "",
      gender: "",
      mobile_1: "",
      mobile_2: "",
      home_tel: "",
      work_tel: "",
      email_1: "",
      email_2: ""
    }],
    address: null,
    has_different_correspondence: false,
    correspondence_address: null,
    lead_staff: "",
    finance_status: "",
    finance_status_notes: "",
    chain_status: "",
    chain_status_notes: "",
    branches: [],
    consent_property_email: false,
    consent_property_sms: false,
    consent_marketing_email: false,
    consent_method: "",
    consent_notes: "",
    exclude_viewing_email: false,
    exclude_viewing_sms: false,
    exclude_appointment_email: false,
    exclude_appointment_sms: false,
    notes: ""
  });
  const [taData, setTaData] = useState({
    // Property & parties
    property_address: "",
    // Landlord
    landlord_name: "",
    landlord_email: "",
    landlord_address: "",
    // Tenants (array for multiple)
    tenants: [{ name: "", email: "", phone: "", address: "" }],
    // Terms
    tenancy_start_date: "",
    tenancy_end_date: "",
    tenancy_term_months: 12,
    rent_amount: "",
    rent_payment_day: "1",
    deposit_amount: "",
    // Additional
    permitted_occupiers: "",
    break_clause: "After 4 months, either party may give 2 months notice",
    additional_terms: "",
    // Send options
    send_copy_to_agency: true
  });

  useEffect(() => {
    fetchData();
  }, [tenantId]);

  useEffect(() => {
    // Pre-fill data when tenant/property/landlord loads
    if (tenant && property && landlord) {
      setTaData(prev => ({
        ...prev,
        property_address: property.address || "",
        landlord_name: landlord.name || "",
        landlord_email: landlord.email || "",
        landlord_address: landlord.address || "",
        tenants: [{
          name: tenant.name || "",
          email: tenant.email || "",
          phone: tenant.phone || "",
          address: tenant.address || property.address || ""
        }],
        rent_amount: property.monthly_rent || "",
        deposit_amount: property.monthly_rent ? (property.monthly_rent * 5 / 4).toFixed(2) : "" // 5 weeks
      }));
    }
  }, [tenant, property, landlord]);

  // Pre-fill applicant data from tenant record
  useEffect(() => {
    if (tenant) {
      // Check if tenant has enhanced applicant_data
      if (tenant.applicant_data) {
        setApplicantData(tenant.applicant_data);
      } else {
        // Convert basic tenant info to applicant format
        const nameParts = (tenant.name || "").split(" ");
        setApplicantData(prev => ({
          ...prev,
          contacts: [{
            ...prev.contacts[0],
            first_name: nameParts[0] || "",
            last_name: nameParts.slice(1).join(" ") || "",
            email_1: tenant.email || "",
            mobile_1: tenant.phone || ""
          }]
        }));
      }
    }
  }, [tenant]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const tenantRes = await axios.get(`${API}/lettings/tenants/${tenantId}`, { headers });
      setTenant(tenantRes.data);
      setEditData(tenantRes.data);
      
      // Get property if linked
      if (tenantRes.data.property_id) {
        const propRes = await axios.get(`${API}/lettings/properties/${tenantRes.data.property_id}`, { headers }).catch(() => null);
        if (propRes?.data) {
          setProperty(propRes.data);
          
          // Get landlord
          if (propRes.data.landlord_id) {
            const llRes = await axios.get(`${API}/lettings/landlords/${propRes.data.landlord_id}`, { headers }).catch(() => null);
            if (llRes?.data) setLandlord(llRes.data);
          }
        }
      }
      
      // Get documents
      const docsRes = await axios.get(`${API}/documents?recipient_id=${tenantId}&limit=10`, { headers }).catch(() => ({ data: { documents: [] } }));
      setDocuments(docsRes.data.documents || []);
      
      // Get available properties for convert modal (only if applicant)
      if (tenantRes.data.status === "looking") {
        const propsRes = await axios.get(`${API}/lettings/properties`, { headers }).catch(() => ({ data: { properties: [] } }));
        // Filter to vacant properties
        const vacantProps = (propsRes.data.properties || []).filter(p => p.status === "vacant" || !p.tenant_id);
        setProperties(vacantProps);
      }
      
    } catch (error) {
      console.error("Error fetching tenant:", error);
      toast.error("Failed to load tenant details");
    } finally {
      setLoading(false);
    }
  };

  // Handle Convert Applicant to Tenant
  const handleConvertToTenant = async () => {
    if (!convertData.property_id) {
      toast.error("Please select a property");
      return;
    }
    if (!convertData.tenancy_start) {
      toast.error("Please enter tenancy start date");
      return;
    }
    
    setConverting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Update tenant status and link to property
      await axios.post(
        `${API}/lettings/tenants/${tenantId}/convert`,
        {
          property_id: convertData.property_id,
          rent_amount: parseFloat(convertData.rent_amount) || 0,
          tenancy_start: convertData.tenancy_start,
          tenancy_end: convertData.tenancy_end || null,
          deposit_amount: parseFloat(convertData.deposit_amount) || 0,
          send_invite: convertData.send_invite
        },
        { headers }
      );
      
      toast.success("Applicant converted to tenant successfully!");
      if (convertData.send_invite && tenant.email) {
        toast.success("Portal invite sent automatically");
      }
      setShowConvertModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert applicant");
    } finally {
      setConverting(false);
    }
  };

  const addTenant = () => {
    setTaData(prev => ({
      ...prev,
      tenants: [...prev.tenants, { name: "", email: "", phone: "", address: "" }]
    }));
  };

  const removeTenant = (index) => {
    if (taData.tenants.length <= 1) return;
    setTaData(prev => ({
      ...prev,
      tenants: prev.tenants.filter((_, i) => i !== index)
    }));
  };

  const updateTenant = (index, field, value) => {
    setTaData(prev => ({
      ...prev,
      tenants: prev.tenants.map((t, i) => i === index ? { ...t, [field]: value } : t)
    }));
  };

  const handleSendTA = async () => {
    // Validation
    if (!taData.landlord_email) {
      toast.error("Landlord email is required");
      return;
    }
    if (!taData.tenants[0]?.email) {
      toast.error("At least one tenant email is required");
      return;
    }
    if (!taData.rent_amount || !taData.tenancy_start_date) {
      toast.error("Please fill in rent amount and start date");
      return;
    }

    setSendingTA(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/documents/send-tenancy-agreement`, {
        property_id: property?.property_id,
        property_address: taData.property_address,
        // Landlord
        landlord_id: landlord?.landlord_id,
        landlord_name: taData.landlord_name,
        landlord_email: taData.landlord_email,
        landlord_address: taData.landlord_address,
        // Tenants
        tenants: taData.tenants.filter(t => t.name && t.email),
        // Terms
        tenancy_start_date: taData.tenancy_start_date,
        tenancy_end_date: taData.tenancy_end_date,
        tenancy_term_months: parseInt(taData.tenancy_term_months),
        rent_amount: parseFloat(taData.rent_amount),
        rent_payment_day: taData.rent_payment_day,
        deposit_amount: parseFloat(taData.deposit_amount),
        // Additional
        permitted_occupiers: taData.permitted_occupiers,
        break_clause: taData.break_clause,
        additional_terms: taData.additional_terms,
        send_copy_to_agency: taData.send_copy_to_agency
      }, { headers });

      toast.success("Tenancy Agreement sent for signatures");
      setShowTAModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send agreement");
    } finally {
      setSendingTA(false);
    }
  };

  const handleSaveApplicantDetails = async () => {
    setSavingApplicant(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build display name from contacts
      const contacts = applicantData.contacts || [];
      let displayName = tenant.name;
      if (contacts.length > 0 && contacts[0].first_name) {
        const names = contacts.map(c => {
          const title = c.title || "";
          return `${title} ${c.first_name} ${c.last_name}`.trim();
        });
        displayName = names.length === 1 ? names[0] : 
                      names.length === 2 ? `${names[0]} & ${names[1]}` :
                      names.slice(0, -1).join(", ") + ` & ${names[names.length - 1]}`;
      }
      
      // Update tenant with enhanced applicant data
      await axios.put(`${API}/lettings/tenants/${tenantId}`, {
        ...editData,
        name: displayName,
        email: contacts[0]?.email_1 || tenant.email,
        phone: contacts[0]?.mobile_1 || tenant.phone,
        applicant_data: applicantData
      }, { headers });
      
      toast.success("Tenant details updated");
      setShowApplicantModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save applicant details");
    } finally {
      setSavingApplicant(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/tenants/${tenantId}`, editData, { headers });
      toast.success("Tenant details updated");
      setTenant(editData);
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveTenant = async () => {
    setArchiving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/lettings/tenants/${tenantId}/archive`, {}, { headers });
      toast.success("Tenant archived successfully");
      setShowArchiveModal(false);
      // Navigate back to lettings or property page
      if (property?.property_id) {
        navigate(`/admin/lettings/property/${property.property_id}`);
      } else {
        navigate("/admin/lettings");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive tenant");
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium">Tenant not found</p>
          <Button onClick={() => navigate("/admin/lettings")} className="mt-4">
            Back to Lettings
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-800">{tenant.name}</h1>
                  {tenant.status === "looking" && (
                    <Badge className="bg-blue-100 text-blue-700">Applicant</Badge>
                  )}
                  {tenant.status === "active" && (
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {tenant.status === "looking" ? "Looking for Property" : "Tenant Profile"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {editing ? (
                <>
                  <Button variant="outline" onClick={() => { setEditing(false); setEditData(tenant); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </>
              ) : (
                <>
                  {/* Convert to Tenant button - only for applicants */}
                  {tenant.status === "looking" && (
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setShowConvertModal(true)}
                      data-testid="convert-to-tenant-btn"
                    >
                      <Home className="w-4 h-4 mr-2" />
                      Convert to Tenant
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setShowApplicantModal(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Full Details
                  </Button>
                  {tenant.status === "active" && (
                    <Button variant="outline" onClick={() => setShowTAModal(true)}>
                      <Send className="w-4 h-4 mr-2" />
                      Send Tenancy Agreement
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setEditing(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Quick Edit
                  </Button>
                  {tenant.status !== "archived" && (
                    <Button 
                      variant="outline" 
                      onClick={() => setShowArchiveModal(true)}
                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                      data-testid="archive-tenant-btn"
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Archive
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Contact Info */}
          <div className="space-y-6">
            {/* Contact Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Contact Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name</Label>
                        <Input
                          value={editData.name || ""}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          data-testid="tenant-name-input"
                        />
                      </div>
                      <div>
                        <Label>Friendly Name</Label>
                        <Input
                          value={editData.friendly_name || ""}
                          onChange={(e) => setEditData({ ...editData, friendly_name: e.target.value })}
                          placeholder="e.g. Bob"
                          data-testid="tenant-friendly-name-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={editData.email || ""}
                          onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                          data-testid="tenant-email-input"
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={editData.phone || ""}
                          onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                          data-testid="tenant-phone-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Mobile 2</Label>
                        <Input
                          value={editData.mobile_2 || ""}
                          onChange={(e) => setEditData({ ...editData, mobile_2: e.target.value })}
                          placeholder="Alternative mobile"
                        />
                      </div>
                      <div>
                        <Label>Work Phone</Label>
                        <Input
                          value={editData.work_phone || ""}
                          onChange={(e) => setEditData({ ...editData, work_phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>National Insurance Number</Label>
                      <Input
                        value={editData.ni_number || ""}
                        onChange={(e) => setEditData({ ...editData, ni_number: e.target.value })}
                        placeholder="e.g. QQ 12 34 56 C"
                      />
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm font-medium mb-2">Emergency Contact</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Emergency Contact Name</Label>
                          <Input
                            value={editData.emergency_contact_name || ""}
                            onChange={(e) => setEditData({ ...editData, emergency_contact_name: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Emergency Contact Phone</Label>
                          <Input
                            value={editData.emergency_contact_phone || ""}
                            onChange={(e) => setEditData({ ...editData, emergency_contact_phone: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm font-medium mb-2">Forwarding Address</p>
                      <Textarea
                        value={editData.forwarding_address || ""}
                        onChange={(e) => setEditData({ ...editData, forwarding_address: e.target.value })}
                        rows={2}
                        placeholder="Enter forwarding address for after tenancy ends"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3 py-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{tenant.email || "No email"}</span>
                    </div>
                    <div className="flex items-center gap-3 py-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{tenant.phone || "No phone"}</span>
                    </div>
                    {tenant.mobile_2 && (
                      <div className="flex items-center gap-3 py-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{tenant.mobile_2} (Mobile 2)</span>
                      </div>
                    )}
                    {tenant.ni_number && (
                      <div className="flex items-center gap-3 py-2">
                        <Shield className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">NI: {tenant.ni_number}</span>
                      </div>
                    )}
                    {tenant.emergency_contact_name && (
                      <div className="flex items-center gap-3 py-2 border-t pt-2">
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                        <span className="text-sm">
                          Emergency: {tenant.emergency_contact_name} - {tenant.emergency_contact_phone}
                        </span>
                      </div>
                    )}
                    {tenant.forwarding_address && (
                      <div className="flex items-start gap-3 py-2 border-t pt-2">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <span className="text-sm">Forwarding: {tenant.forwarding_address}</span>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Tenancy Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                  Tenancy Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Status</Label>
                        <Select
                          value={editData.status || "active"}
                          onValueChange={(value) => setEditData({ ...editData, status: value })}
                        >
                          <SelectTrigger data-testid="tenant-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="looking">Applicant/Looking</SelectItem>
                            <SelectItem value="active">Active Tenant</SelectItem>
                            <SelectItem value="notice">Notice Given</SelectItem>
                            <SelectItem value="vacated">Vacated</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Tenancy Reference</Label>
                        <Input
                          value={editData.tenancy_reference || ""}
                          onChange={(e) => setEditData({ ...editData, tenancy_reference: e.target.value })}
                          placeholder="e.g. #L0999"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={editData.start_date ? (editData.start_date.includes('/') 
                            ? editData.start_date.split('/').reverse().join('-') 
                            : editData.start_date.split('T')[0]) : ""}
                          onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                          data-testid="tenant-start-date-input"
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={editData.end_date ? (editData.end_date.includes('/') 
                            ? editData.end_date.split('/').reverse().join('-') 
                            : editData.end_date.split('T')[0]) : ""}
                          onChange={(e) => setEditData({ ...editData, end_date: e.target.value })}
                          data-testid="tenant-end-date-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Rent Amount (£)</Label>
                        <Input
                          type="number"
                          value={editData.rent_amount || ""}
                          onChange={(e) => setEditData({ ...editData, rent_amount: parseFloat(e.target.value) || null })}
                          data-testid="tenant-rent-input"
                        />
                      </div>
                      <div>
                        <Label>Rent Frequency</Label>
                        <Select
                          value={editData.rent_frequency || "pcm"}
                          onValueChange={(value) => setEditData({ ...editData, rent_frequency: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="fortnightly">Fortnightly</SelectItem>
                            <SelectItem value="pcm">Monthly (PCM)</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="annual">Annual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is-periodic"
                        checked={editData.is_periodic || false}
                        onChange={(e) => setEditData({ ...editData, is_periodic: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="is-periodic" className="cursor-pointer">
                        Periodic Tenancy (rolling contract)
                      </Label>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>
                        {tenant.status || 'Unknown'}
                      </Badge>
                    </div>
                    {tenant.tenancy_reference && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-sm text-muted-foreground">Reference</span>
                        <span className="text-sm font-mono">{tenant.tenancy_reference}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Start Date</span>
                      <span className="text-sm">{tenant.start_date || "Not set"}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">End Date</span>
                      <span className="text-sm">{tenant.end_date || "Not set"}</span>
                    </div>
                    {tenant.rent_amount && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-sm text-muted-foreground">Rent</span>
                        <span className="text-sm font-semibold text-emerald-600">
                          £{tenant.rent_amount?.toLocaleString()} {tenant.rent_frequency || 'pcm'}
                        </span>
                      </div>
                    )}
                    {tenant.is_periodic && (
                      <div className="flex justify-between py-2">
                        <span className="text-sm text-muted-foreground">Type</span>
                        <Badge variant="outline">Periodic Tenancy</Badge>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Property */}
            {property && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Home className="w-5 h-5 text-emerald-600" />
                    Property
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-medium">{property.address}</p>
                  <p className="text-sm text-muted-foreground">{property.postcode}</p>
                  {property.monthly_rent && (
                    <p className="mt-2 text-emerald-600 font-semibold">£{property.monthly_rent}/month</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Landlord */}
            {landlord && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-600" />
                    Landlord
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-medium">{landlord.name}</p>
                  <p className="text-sm text-muted-foreground">{landlord.email}</p>
                </CardContent>
              </Card>
            )}

            {/* Deposit Protection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-emerald-600" />
                  Deposit Protection
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {editing ? (
                  <>
                    <div>
                      <Label>Deposit Amount (£)</Label>
                      <Input
                        type="number"
                        value={editData.deposit || ""}
                        onChange={(e) => setEditData({ ...editData, deposit: parseFloat(e.target.value) || null })}
                      />
                    </div>
                    <div>
                      <Label>Scheme</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        value={editData.deposit_scheme || ""}
                        onChange={(e) => setEditData({ ...editData, deposit_scheme: e.target.value })}
                      >
                        <option value="">Select scheme</option>
                        <option value="DPS">DPS (Deposit Protection Service)</option>
                        <option value="TDS">TDS (Tenancy Deposit Scheme)</option>
                        <option value="MyDeposits">MyDeposits</option>
                      </select>
                    </div>
                    <div>
                      <Label>Protected Date</Label>
                      <Input
                        type="date"
                        value={editData.deposit_protected_date || ""}
                        onChange={(e) => setEditData({ ...editData, deposit_protected_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Certificate Number</Label>
                      <Input
                        value={editData.deposit_certificate_number || ""}
                        onChange={(e) => setEditData({ ...editData, deposit_certificate_number: e.target.value })}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Amount</span>
                      <span className="font-semibold text-emerald-600">
                        {tenant.deposit ? `£${tenant.deposit.toLocaleString()}` : "Not set"}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Scheme</span>
                      <span className="text-sm font-medium">{tenant.deposit_scheme || "Not recorded"}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Protected Date</span>
                      <span className="text-sm">
                        {tenant.deposit_protected_date 
                          ? new Date(tenant.deposit_protected_date).toLocaleDateString('en-GB')
                          : "Not recorded"}
                      </span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-sm text-muted-foreground">Certificate No.</span>
                      <span className="text-sm font-mono">{tenant.deposit_certificate_number || "Not recorded"}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Documents */}
          <div className="lg:col-span-2 space-y-6">
            {/* Documents */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Documents
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setShowTAModal(true)}>
                    <Send className="w-4 h-4 mr-2" />
                    Send Tenancy Agreement
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No documents sent yet</p>
                ) : (
                  <div className="space-y-3">
                    {documents.map(doc => (
                      <div 
                        key={doc.document_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            doc.status === 'signed' ? 'bg-green-100' : doc.status === 'viewed' ? 'bg-amber-100' : 'bg-slate-100'
                          }`}>
                            {doc.status === 'signed' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : doc.status === 'viewed' ? (
                              <Clock className="w-4 h-4 text-amber-600" />
                            ) : (
                              <Send className="w-4 h-4 text-slate-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">
                              {doc.document_type === 'tenancy_agreement' ? 'Tenancy Agreement' : doc.document_type}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(doc.created_at).toLocaleDateString('en-GB')}
                            </p>
                          </div>
                        </div>
                        <Badge variant={doc.status === 'signed' ? 'default' : 'secondary'}>
                          {doc.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Send Tenancy Agreement Modal */}
      <Dialog open={showTAModal} onOpenChange={setShowTAModal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Tenancy Agreement
            </DialogTitle>
            <DialogDescription>
              Review and edit details. Agreement will be sent to landlord and all tenants for signature.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Property */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <Label className="text-blue-800 font-semibold">Property Address</Label>
              <Input
                value={taData.property_address}
                onChange={(e) => setTaData({ ...taData, property_address: e.target.value })}
                className="mt-2 bg-white"
              />
            </div>

            {/* Landlord */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Landlord (will receive for signature)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={taData.landlord_name}
                    onChange={(e) => setTaData({ ...taData, landlord_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={taData.landlord_email}
                    onChange={(e) => setTaData({ ...taData, landlord_email: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={taData.landlord_address}
                    onChange={(e) => setTaData({ ...taData, landlord_address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Tenants */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Tenant(s) (will receive for signature)
                </h4>
                <Button variant="outline" size="sm" onClick={addTenant}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Tenant
                </Button>
              </div>
              
              {taData.tenants.map((t, idx) => (
                <div key={idx} className="border rounded-lg p-4 mb-3 bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-sm">Tenant {idx + 1}</span>
                    {taData.tenants.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeTenant(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={t.name}
                        onChange={(e) => updateTenant(idx, 'name', e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={t.email}
                        onChange={(e) => updateTenant(idx, 'email', e.target.value)}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={t.phone}
                        onChange={(e) => updateTenant(idx, 'phone', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Current Address</Label>
                      <Input
                        value={t.address}
                        onChange={(e) => updateTenant(idx, 'address', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Terms */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Tenancy Terms
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={taData.tenancy_start_date}
                    onChange={(e) => setTaData({ ...taData, tenancy_start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={taData.tenancy_end_date}
                    onChange={(e) => setTaData({ ...taData, tenancy_end_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Term (months)</Label>
                  <Select
                    value={String(taData.tenancy_term_months)}
                    onValueChange={(v) => setTaData({ ...taData, tenancy_term_months: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="18">18 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rent Payment Day</Label>
                  <Select
                    value={taData.rent_payment_day}
                    onValueChange={(v) => setTaData({ ...taData, rent_payment_day: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,5,10,15,20,25,28].map(d => (
                        <SelectItem key={d} value={String(d)}>{d}th of month</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Financials */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <PoundSterling className="w-4 h-4" />
                Rent & Deposit
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monthly Rent (£) *</Label>
                  <Input
                    type="number"
                    value={taData.rent_amount}
                    onChange={(e) => setTaData({ ...taData, rent_amount: e.target.value })}
                    placeholder="e.g. 1500"
                  />
                </div>
                <div>
                  <Label>Deposit (£)</Label>
                  <Input
                    type="number"
                    value={taData.deposit_amount}
                    onChange={(e) => setTaData({ ...taData, deposit_amount: e.target.value })}
                    placeholder="Max 5 weeks rent"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Protected via DPS</p>
                </div>
              </div>
            </div>

            {/* Additional */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3">Additional Terms</h4>
              <div className="space-y-4">
                <div>
                  <Label>Permitted Occupiers (other than tenants)</Label>
                  <Input
                    value={taData.permitted_occupiers}
                    onChange={(e) => setTaData({ ...taData, permitted_occupiers: e.target.value })}
                    placeholder="e.g. None, or list names"
                  />
                </div>
                <div>
                  <Label>Break Clause</Label>
                  <Input
                    value={taData.break_clause}
                    onChange={(e) => setTaData({ ...taData, break_clause: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Additional Terms / Special Conditions</Label>
                  <Textarea
                    value={taData.additional_terms}
                    onChange={(e) => setTaData({ ...taData, additional_terms: e.target.value })}
                    rows={3}
                    placeholder="Any additional terms..."
                  />
                </div>
              </div>
            </div>

            {/* Send Options */}
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Copies will be sent to:</strong>
              </p>
              <ul className="mt-2 text-sm text-green-700 space-y-1">
                <li>✓ {taData.landlord_name || "Landlord"} ({taData.landlord_email || "email"})</li>
                {taData.tenants.filter(t => t.email).map((t, i) => (
                  <li key={i}>✓ {t.name || `Tenant ${i+1}`} ({t.email})</li>
                ))}
                <li>✓ Goodacres Residential (office copy)</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowTAModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendTA} disabled={sendingTA}>
              {sendingTA ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send for Signatures
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Applicant Details Modal */}
      <Dialog open={showApplicantModal} onOpenChange={setShowApplicantModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Full Tenant Details
            </DialogTitle>
            <DialogDescription>
              Edit comprehensive tenant information including contact details, consents, and preferences.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="details" className="mt-4">
            <TabsList className="w-full grid grid-cols-4">
              <TabsTrigger value="details">
                <User className="w-4 h-4 mr-2" />
                Details
              </TabsTrigger>
              <TabsTrigger value="status">
                <Settings className="w-4 h-4 mr-2" />
                Status
              </TabsTrigger>
              <TabsTrigger value="consents">
                <Shield className="w-4 h-4 mr-2" />
                Consents
              </TabsTrigger>
              <TabsTrigger value="reminders">
                <BellOff className="w-4 h-4 mr-2" />
                Reminders
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-4">
              <ApplicantForm
                value={applicantData}
                onChange={setApplicantData}
                label="Tenant"
                showBusinessOption={true}
                showCorrespondenceAddress={true}
                showStatusFields={false}
                showMarketingConsents={false}
                showReminderExclusions={false}
              />
            </TabsContent>

            <TabsContent value="status" className="mt-4">
              <ApplicantForm
                value={applicantData}
                onChange={setApplicantData}
                label="Tenant"
                showBusinessOption={false}
                showCorrespondenceAddress={false}
                showStatusFields={true}
                showMarketingConsents={false}
                showReminderExclusions={false}
              />
            </TabsContent>

            <TabsContent value="consents" className="mt-4">
              <ApplicantForm
                value={applicantData}
                onChange={setApplicantData}
                label="Tenant"
                showBusinessOption={false}
                showCorrespondenceAddress={false}
                showStatusFields={false}
                showMarketingConsents={true}
                showReminderExclusions={false}
              />
            </TabsContent>

            <TabsContent value="reminders" className="mt-4">
              <ApplicantForm
                value={applicantData}
                onChange={setApplicantData}
                label="Tenant"
                showBusinessOption={false}
                showCorrespondenceAddress={false}
                showStatusFields={false}
                showMarketingConsents={false}
                showReminderExclusions={true}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter className="border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowApplicantModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveApplicantDetails} disabled={savingApplicant}>
              {savingApplicant ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Details
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Applicant to Tenant Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-emerald-600" />
              Convert to Tenant
            </DialogTitle>
            <DialogDescription>
              Assign {tenant?.name} to a property and convert them from applicant to active tenant.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Property Selection */}
            <div>
              <Label>Select Property *</Label>
              <Select
                value={convertData.property_id}
                onValueChange={(v) => {
                  const selectedProp = properties.find(p => p.property_id === v);
                  setConvertData({ 
                    ...convertData, 
                    property_id: v,
                    rent_amount: selectedProp?.rent_amount || selectedProp?.monthly_rent || ""
                  });
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a vacant property..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.length === 0 ? (
                    <div className="p-2 text-center text-muted-foreground text-sm">
                      No vacant properties available
                    </div>
                  ) : (
                    properties.map(prop => (
                      <SelectItem key={prop.property_id} value={prop.property_id}>
                        {prop.address || prop.address_line1} - {prop.postcode}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Tenancy Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tenancy Start Date *</Label>
                <Input
                  type="date"
                  value={convertData.tenancy_start}
                  onChange={(e) => setConvertData({ ...convertData, tenancy_start: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Tenancy End Date</Label>
                <Input
                  type="date"
                  value={convertData.tenancy_end}
                  onChange={(e) => setConvertData({ ...convertData, tenancy_end: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Financial Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Rent (£)</Label>
                <Input
                  type="number"
                  value={convertData.rent_amount}
                  onChange={(e) => setConvertData({ ...convertData, rent_amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Deposit Amount (£)</Label>
                <Input
                  type="number"
                  value={convertData.deposit_amount}
                  onChange={(e) => setConvertData({ ...convertData, deposit_amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Auto Invite Toggle */}
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <input
                type="checkbox"
                id="send_invite"
                checked={convertData.send_invite}
                onChange={(e) => setConvertData({ ...convertData, send_invite: e.target.checked })}
                className="rounded w-4 h-4"
              />
              <div>
                <Label htmlFor="send_invite" className="font-medium text-emerald-800">
                  Send Portal Invite
                </Label>
                <p className="text-xs text-emerald-600">
                  {tenant?.email 
                    ? `Automatically send invite to ${tenant.email}` 
                    : "No email address - invite cannot be sent"}
                </p>
              </div>
            </div>

            {/* Requirements Note */}
            {tenant?.requirements && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800">Applicant Requirements:</p>
                <p className="text-sm text-blue-600 mt-1">{tenant.requirements}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertToTenant} 
              disabled={converting || !convertData.property_id}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {converting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Convert & Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Tenant Modal */}
      <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              Archive Tenant
            </DialogTitle>
            <DialogDescription>
              Archive this tenant and save their record to the property's tenant history.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>What happens when you archive:</strong>
              </p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
                <li>Tenant record is saved to property's tenant history</li>
                <li>Tenant is unlinked from the property</li>
                <li>Property becomes available for new tenants</li>
                <li>You can view past tenant records on the property page</li>
              </ul>
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium">Archiving:</p>
              <p className="text-lg font-semibold">{tenant?.name}</p>
              {property && (
                <p className="text-sm text-muted-foreground mt-1">
                  From: {property.address}
                </p>
              )}
              {tenant?.start_date && (
                <p className="text-xs text-muted-foreground mt-1">
                  Tenancy: {tenant.start_date} - {tenant.end_date || "Present"}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleArchiveTenant}
              disabled={archiving}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-archive-btn"
            >
              {archiving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Archive className="w-4 h-4 mr-2" />
              )}
              Archive Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDetailPage;
